import { logger } from '@belong/logging/logger';
import { ContentfulClientApi, createClient } from 'contentful';
import resolveResponse from 'contentful-resolve-response';
import { getConfig } from '@belong/configs/next/config';

const { publicRuntimeConfig } = getConfig();
const { isTest } = publicRuntimeConfig.env;

const debugContentfulError = (query: any, error: string, statusCode: number): void => {
  if (isTest) {
    return;
  }

  logger.error('StatusCode:', statusCode);
  logger.error('Operation:', query);
  logger.error('Error:', error);
};

type ContentAPIError = {
  error: string;
  statusCode: number;
};

const CONTENT_TYPE_FIELD = 'content_type';

export class ContentAPI {
  public space: ContentfulClientApi = createClient(publicRuntimeConfig.contentful);

  async getContentfulEntries(query: any): Promise<any> {
    const data = await this.space
      .getEntries(query)
      .catch((err: any) => ({ error: err || 'Request Failed', statusCode: err?.response?.status }));
    const { error, statusCode } = data as ContentAPIError;

    if (error) {
      debugContentfulError(query, error, statusCode);
      return {
        statusCode: statusCode || 404,
        error
      };
    }

    return resolveResponse(data);
  }

  async getConfig(): Promise<any> {
    return this.getContentfulEntries({
      [CONTENT_TYPE_FIELD]: 'supportConfig',
      include: 10
    });
  }

  async getArticle(categoryId, slug: string): Promise<any> {
    return this.getContentfulEntries({
      [CONTENT_TYPE_FIELD]: 'supportArticle',
      'fields.categories[in]': categoryId,
      'fields.metaPageSlug': slug,
      include: 10
    });
  }

  async getArticleLinks(categoryId: string): Promise<any> {
    return this.getContentfulEntries({
      [CONTENT_TYPE_FIELD]: 'supportArticle',
      'fields.categories[in]': categoryId,
      select: 'fields.title,fields.metaPageSlug'
    });
  }

  async getPage(url: string): Promise<any> {
    return this.getContentfulEntries({
      [CONTENT_TYPE_FIELD]: 'page',
      'fields.pageUrl.sys.contentType.sys.id': 'urlInternal',
      'fields.pageUrl.fields.url': url,
      include: 10
    });
  }

  async getSections(sections: string[]): Promise<any> {
    return this.getContentfulEntries({
      'sys.id[in]': Object.values(sections).join(','),
      include: 10
    });
  }
}

export default new ContentAPI();
